import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, EmptyState, Field, SectionHeading, Select, TextInput } from '@/components/ui';
import { formatDate, formatTime, startOfMonth, todayIso } from '@/domain/dates';
import { formatEur, formatKm } from '@/domain/money';
import type { Trip } from '@/domain/types';
import { tripsInPeriod } from '@/db/repositories';
import { useSession } from '@/session/SessionContext';
import { passengerDisplayName } from '@/suggest';
import { useT } from '@/i18n';

export function TripListScreen() {
  const session = useSession();
  const t = useT();
  const [from, setFrom] = useState(startOfMonth(todayIso()));
  const [to, setTo] = useState(todayIso());
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'alle' | 'offen' | 'abgerechnet'>('alle');
  const [trips, setTrips] = useState<Trip[] | null>(null);

  useEffect(() => {
    void (async () => setTrips(await tripsInPeriod(session.dataKey, from, to)))();
  }, [session.dataKey, from, to]);

  const passengerById = useMemo(
    () => new Map(session.passengers.map((passenger) => [passenger.id, passenger])),
    [session.passengers],
  );
  const addressById = useMemo(
    () => new Map(session.addresses.map((address) => [address.id, address])),
    [session.addresses],
  );

  const filtered = useMemo(() => {
    if (!trips) return null;
    const needle = query.trim().toLowerCase();
    return trips
      .filter((trip) => {
        if (status === 'offen' && trip.invoiceId) return false;
        if (status === 'abgerechnet' && !trip.invoiceId) return false;
        if (!needle) return true;
        const passenger = passengerById.get(trip.passengerId);
        const haystack = [
          passenger ? passengerDisplayName(passenger) : '',
          addressById.get(trip.fromAddressId)?.label ?? '',
          addressById.get(trip.toAddressId)?.label ?? '',
          trip.comment ?? '',
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(needle);
      })
      .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  }, [trips, query, status, passengerById, addressById]);

  const totals = useMemo(() => {
    if (!filtered) return null;
    return {
      count: filtered.length,
      distanceMeters: filtered.reduce((sum, trip) => sum + trip.distanceMeters, 0),
      grossCents: filtered.reduce((sum, trip) => sum + trip.fareGrossCents, 0),
    };
  }, [filtered]);

  return (
    <div className="space-y-5">
      <SectionHeading
        title={t.trips.title}
        description={t.trips.description}
        action={
          <Link to="/fahrten/neu" className="btn-primary">
            {t.trips.new}
          </Link>
        }
      />

      <Card>
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label={t.common.from}>
            <TextInput type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </Field>
          <Field label={t.common.to}>
            <TextInput type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </Field>
          <Field label={t.common.status}>
            <Select
              value={status}
              onChange={(event) => setStatus(event.target.value as typeof status)}
            >
              <option value="alle">{t.common.all}</option>
              <option value="offen">{t.trips.filterOpen}</option>
              <option value="abgerechnet">{t.trips.filterInvoiced}</option>
            </Select>
          </Field>
          <Field label={t.common.search}>
            <TextInput
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t.trips.searchPlaceholder}
            />
          </Field>
        </div>
      </Card>

      {totals && totals.count > 0 && (
        <div className="text-muted flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <span>
            <span className="text-heading font-semibold">{totals.count}</span>{' '}
            {t.trips.totalTrips}
          </span>
          <span>
            <span className="text-heading font-semibold">{formatKm(totals.distanceMeters)}</span> km
          </span>
          <span>
            <span className="text-heading font-semibold">{formatEur(totals.grossCents)}</span>{' '}
            {t.trips.totalGross}
          </span>
        </div>
      )}

      {!filtered ? (
        <p className="text-muted text-sm">{t.common.loading}</p>
      ) : filtered.length === 0 ? (
        <EmptyState title={t.trips.empty}>
          <Link to="/fahrten/neu" className="text-accent underline">
            {t.trips.firstTrip}
          </Link>
        </EmptyState>
      ) : (
        <ul className="space-y-2">
          {filtered.map((trip) => {
            const passenger = passengerById.get(trip.passengerId);
            return (
              <li key={trip.id}>
                <Link
                  to={`/fahrten/${trip.id}`}
                  className="card hover:border-line-strong block transition-colors"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-heading truncate text-sm font-medium">
                        {passenger ? passengerDisplayName(passenger) : t.common.unknown}
                      </p>
                      <p className="text-muted mt-0.5 truncate text-xs">
                        {addressById.get(trip.fromAddressId)?.label ?? '?'} →{' '}
                        {addressById.get(trip.toAddressId)?.label ?? '?'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-heading text-sm font-semibold">
                        {formatEur(trip.fareGrossCents)}
                      </p>
                      <p className="text-muted text-xs">{formatKm(trip.distanceMeters)} km</p>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-muted text-xs">
                      {formatDate(trip.date)} · {formatTime(trip.startedAt)}
                    </span>
                    {trip.lockedAt && <Badge tone="info">{t.trips.badgeLocked}</Badge>}
                    {trip.distanceOverridden && (
                      <Badge tone="warning">{t.trips.badgeDistanceFixed}</Badge>
                    )}
                    {trip.invoiceId ? (
                      <Badge tone="success">{t.trips.badgeInvoiced}</Badge>
                    ) : (
                      <Badge>{t.trips.badgeOpen}</Badge>
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
